import React, { FunctionComponent, useContext, useRef, useState } from "react";
import { Draggable, Droppable } from 'react-beautiful-dnd';
import produce from "immer";

import { ConfirmCard } from "./ConfirmCard";
import { FilterModal } from "./FilterModal";
import { FiltersList } from "./FiltersList";
import { NotificationsCard } from "./NotificationsCard";
import { StageCard } from "./StageCard";
import { UpdateInfoCard } from "./UpdateInfoCard";
import { UpdateInfoModal } from "./UpdateInfoModal";
import { UpdatePageModel } from "../UpdatePageModel";
import { AsyncStatus, CardType, DraggableType, UpdateState, UserRole } from "enums";
import { IFilter, IStage } from "interfaces";
import { parseAxiosError, UsersService } from "services";
import { StageModal } from "./StageModal";
import { Button } from "components";
import { BoardContext } from "pages";
import { ConfirmModal } from "./ConfirmModal";
import { NotificationsModal } from "./NotificationsModal";
import { DeleteStageModal } from "./DeleteStageModal";

interface IBoardProps {
    model: UpdatePageModel,
}

interface IBoardState {
    modalStates: {[cardType: string]: IModalState},
}

interface IFilterModalPassedState {
    filter: IFilter,
    isGlobal: boolean,
    isNew: boolean,
    stageId: number,
    stageIndex: number,
}

interface IStageModalPassedState {
    isNew: boolean,
    stage: IStage,
    stageIndex: number,
}

interface IDeleteStageModalPassedState {
    stage: IStage,
}

interface IConfirmStagePassedState {
    stageId: number,
}

interface IModalState {
    show?: boolean
    // Properties for specific modals
    confirmModal?: IConfirmStagePassedState,
    filterModal?: IFilterModalPassedState,
    stageModal?: IStageModalPassedState,
    deleteStageModal?: IDeleteStageModalPassedState,
}

const DeleteStageModalKey = 'DeleteStageModal';

export const Board: FunctionComponent<IBoardProps> = function Board(props) {
    const { model } = props;
    const [state, setState] = useState<IBoardState>({
        modalStates: {},
    })
    const { modalStates } = state;
    const boardContext = useContext(BoardContext);

    // We're using refs as keys to reset the modals when we reopen them.
    const modalRefs = {
        [CardType.Confirm]: useRef(1),
        [CardType.Filter]: useRef(1),
        [CardType.Notifications]: useRef(1),
        [CardType.Stage]: useRef(1),
        [DeleteStageModalKey]: useRef(1),
        [CardType.UpdateInfo]: useRef(1),
    }

    function openEditFilterModal(modalState: IFilterModalPassedState) {
        modalRefs[CardType.Filter].current++;

        setState(produce(draft => {
            draft.modalStates[CardType.Filter] = {
                show: true,
                filterModal: modalState,
            };
        }));
    }

    function openEditStageModal(modalState: IStageModalPassedState) {
        modalRefs[CardType.Stage].current++;
        const stages = model.update.rollout.stages;

        // For a new stage, set the stage's start date to the previous stage's end date
        if (modalState.isNew && stages.length > 0) {
            const lastStage = stages[stages.length - 1];
            const newStage = modalState.stage;

            if (lastStage.stopTime) {
                // Preserve the default duration
                const duration = newStage.stopTime.getTime() - newStage.startTime.getTime();

                newStage.startTime = new Date(lastStage.stopTime.getTime());
                newStage.stopTime = new Date(newStage.startTime.getTime() + duration);
            }
        }

        setState(produce(draft => {
            draft.modalStates[CardType.Stage] = {
                show: true,
                stageModal: modalState,
            };
        }));
    }

    function openDeleteStageModal(stage: IStage) {
        modalRefs[DeleteStageModalKey].current++;
        setState(produce(draft => {
            draft.modalStates[DeleteStageModalKey] = {
                show: true,
                deleteStageModal: {stage: stage},
            };
        }));
    }

    function openConfirmModal(modalState: IConfirmStagePassedState) {
        modalRefs[CardType.Confirm].current++;

        setState(produce(draft => {
            draft.modalStates[CardType.Confirm] = {
                show: true,
                confirmModal: modalState,
            };
        }));
    }

    function openEditCardModal(cardType: CardType) {
        modalRefs[cardType].current++;

        setState(produce(draft => {
            draft.modalStates[cardType] = {
                show: true,
            };
        }));
    }

    function closeModal(cardType: CardType) {
        setState(produce(draft => {
            if (!draft.modalStates[cardType]) {
                draft.modalStates[cardType] = {};
            }
            // We're not clearing any other props to prevent a flash.
            draft.modalStates[cardType].show = false;
        }));
    }

    function cancelScheduleChange() {
        if (!model.isChangingSchedule) return;
        if (model.apiCallStatus === AsyncStatus.Loading) return;
        model.cancelScheduleChange();
    }

    function saveScheduleChange() {
        if (!model.isChangingSchedule) return;
        if (model.apiCallStatus === AsyncStatus.Loading) return;

        model.saveScheduleChange()
            .then(() => {
                boardContext.toastSuccess(`Schedule changed successfully.`);
            })
            .catch((err) => {
                boardContext.toastError(parseAxiosError(err));
            })
    }

    const canEdit = model.update?.state === UpdateState.Draft
        && UsersService.oktaUserInfo?.role >= UserRole.LimitedUser;

    const stages = model.update.rollout.stages;
    const confirmModalState = modalStates[CardType.Confirm];
    const filterModalState = modalStates[CardType.Filter];
    const stageModalState = modalStates[CardType.Stage]
    const deleteStageModalState = modalStates[DeleteStageModalKey];

    return (<>
        {model.isChangingSchedule && (
            <div className="update_boardChangeSchedule">
                Schedule changes must be manually saved after making sure the schedule is valid.
                <Button theme="blueOutline" size="small" onClick={cancelScheduleChange}>
                    Cancel
                </Button>
                <Button theme="blueOutline" size="small" onClick={saveScheduleChange}>
                    Save
                </Button>
            </div>
        )}
        <div className="update_board">
            <div className="update_boardCol first">
                <UpdateInfoCard
                    key="updateInfoCard"
                    model={model}
                    onClick={() => openEditCardModal(CardType.UpdateInfo)}
                />
                <NotificationsCard
                    key="notificationsCard"
                    model={model}
                    onClick={() => openEditCardModal(CardType.Notifications)}
                />
                <FiltersList
                    canEdit={canEdit}
                    filters={model.update?.rollout.global.filters || []}
                    isGlobal={true}
                    key="globalFiltersList"
                    model={model}
                    onEditFilter={((filter, isNew) => openEditFilterModal({
                        filter: filter,
                        isGlobal: true,
                        isNew: isNew,
                        stageId: undefined,
                        stageIndex: undefined,
                    }))}
                    stageId={undefined}
                    stageIndex={undefined}
                />
            </div>
            <Droppable
                droppableId="stages"
                type={DraggableType.Stage}
                direction="horizontal"
            >
                {(dropProvided, dropSnapshot) => (
                    <div
                        className="update_boardStages"
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                    >
                        {stages.map((stage, i) => (                                
                            <Draggable
                                draggableId={model.getDragDropId(DraggableType.Stage, stage.id)}
                                key={'stage_' + stage.id}
                                index={i}
                                isDragDisabled={!canEdit}
                            >
                                {(dragProvided, dragSnapshot) => (
                                    <div
                                        className="update_boardColWrap"
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                    >
                                        {stage.requiresConfirmation && (
                                            <div className="update_boardCol confirmCol">
                                                <ConfirmCard
                                                    model={model}
                                                    stage={stage}
                                                    stageIndex={i}
                                                    onConfirm={() => openConfirmModal({
                                                        stageId: stage.id,
                                                    })}
                                                />
                                            </div>
                                        )}
                                        <div className="update_boardCol">
                                            <StageCard
                                                canEdit={canEdit}
                                                dragHandleProps={dragProvided.dragHandleProps}
                                                model={model}
                                                stage={stage}
                                                stageIndex={i}
                                                onEditFilter={(filter, isNew) => openEditFilterModal({
                                                    filter: filter,
                                                    isGlobal: false,
                                                    isNew: isNew,
                                                    stageId: stage.id,
                                                    stageIndex: i,
                                                })}
                                                onEditStage={(stage) => openEditStageModal({
                                                    isNew: false,
                                                    stage: stage,
                                                    stageIndex: i,
                                                })}
                                                onDeleteStage={openDeleteStageModal}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {dropProvided.placeholder}
                    </div>
                )}
            </Droppable>
            {canEdit && (
                <div className="update_boardCol addStageCol last">
                    <Button
                        theme="blueOutline"
                        size="small"
                        className="update_addStageButton"
                        onClick={() => openEditStageModal({
                            isNew: true,
                            stage: model.createDefaultStage({ isFirst: stages.length === 0 }),
                            stageIndex: stages.length,
                        })}
                    >
                        Add stage
                    </Button>
                </div>
            )}
        </div>
        <ConfirmModal
            key={'confirmModal_' + modalRefs[CardType.Confirm].current}
            model={model}
            onHide={() => closeModal(CardType.Confirm)}
            stageId={confirmModalState?.confirmModal.stageId}
            show={modalStates[CardType.Confirm]?.show}
        />
        <FilterModal
            canEdit={canEdit}
            filter={filterModalState?.filterModal.filter || ({} as IFilter)}
            isGlobal={filterModalState?.filterModal.isGlobal}
            isNew={filterModalState?.filterModal.isNew}
            key={'filterModal_' + modalRefs[CardType.Filter].current}
            model={model}
            onHide={() => closeModal(CardType.Filter)}
            show={filterModalState?.show}
            stageId={filterModalState?.filterModal.stageId}
            stageIndex={filterModalState?.filterModal.stageIndex}
        />
        <NotificationsModal
            canEdit={canEdit}
            key={'notificationsModal_' + modalRefs[CardType.Notifications].current}
            model={model}
            onHide={() => closeModal(CardType.Notifications)}
            show={modalStates[CardType.Notifications]?.show}
        />
        <StageModal
            isNew={stageModalState?.stageModal.isNew}
            key={'stageModal_' + modalRefs[CardType.Stage].current}
            model={model}
            onHide={() => closeModal(CardType.Stage)}
            onOpenDeleteModal={openDeleteStageModal}
            show={stageModalState?.show}
            stage={stageModalState?.stageModal.stage}
            stageIndex={stageModalState?.stageModal.stageIndex}
            onConfirm={() => openConfirmModal({
                stageId: stageModalState?.stageModal.stage.id,
            })}
        />
        <UpdateInfoModal
            canEdit={canEdit}
            key={'updateInfoModal_' + modalRefs[CardType.UpdateInfo].current}
            model={model}
            onHide={() => closeModal(CardType.UpdateInfo)}
            show={modalStates[CardType.UpdateInfo]?.show}
        />
        <DeleteStageModal
            model={model}
            key={'deleteStageModal_' + modalRefs[DeleteStageModalKey].current}
            show={deleteStageModalState?.show}
            stage={deleteStageModalState?.deleteStageModal.stage}
            onCancel={() => closeModal(DeleteStageModalKey as CardType)}
            onSuccess={() => closeModal(DeleteStageModalKey as CardType)}
        />
    </>);
}